import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Link,
  Avatar,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import TermsOfService from '../components/common/TermsOfService';
import PrivacyPolicy from '../components/common/PrivacyPolicy';

const Register = () => {
  const history = useHistory();
  const { register, currentUser } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    role: 'vendor',
    username: '',
    full_name: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [tosDialogOpen, setTosDialogOpen] = useState(false);
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const hasShownSuccess = useRef(false);
  
  // Prevent any redirects when showing success message
  useEffect(() => {
    // If we've shown success, prevent any navigation away from this page
    if (success && hasShownSuccess.current) {
      console.log('✅ Success message is showing - preventing redirects');
      // Block any navigation attempts
      const unblock = history.block((location, action) => {
        if (action === 'PUSH' && !hasShownSuccess.current) {
          return false; // Allow navigation
        }
        // If success is showing, only allow navigation if user explicitly clicks a button
        return 'Are you sure you want to leave? Your verification message will be lost.';
      });
      
      return () => {
        unblock();
      };
    }
  }, [success, history]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!termsAccepted) {
      return setError('You must accept the Terms of Service and Privacy Policy to create an account');
    }
    
    if (formData.password !== formData.confirmPassword) {
      return setError('Passwords do not match');
    }

    try {
      setLoading(true);
      const userData = {
        email: formData.email,
        password: formData.password,
        username: formData.email,
        full_name: formData.full_name || formData.email.split('@')[0],
        role_id: formData.role === 'vendor' ? 1 : 2,
        is_active: true
      };

      // Set flag and success state BEFORE registration to prevent any redirects
      hasShownSuccess.current = true;
      setSuccess(true); // Set immediately to prevent redirects
      
      const result = await register(userData);
      console.log('Registration result:', result);
      console.log('✅ Registration successful! Success state is:', success);
      
      // Ensure error is cleared
      setError('');
      
      // Clear form
      setFormData(prev => ({
        ...prev,
        password: '',
        confirmPassword: '',
      }));
      setTermsAccepted(false);
      
      // Don't redirect immediately - show the verification message
      // User can still proceed to profile setup or dashboard if they want
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.response?.data?.detail?.[0]?.msg || 'Failed to create an account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container component="main" sx={{ height: '100vh' }}>
      {/* Left side - Image */}
      <Grid
        item
        xs={false}
        sm={4}
        md={7}
        sx={{
          backgroundImage: 'url(/images/pattern.png)',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, #ff9800 30%, #ff5722 90%)',
            opacity: 0.9,
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            textAlign: 'center',
            px: 4,
          }}
        >
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
           Welcome to Vendoroll
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
          Your one-stop platform for connecting vendors with events
        </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              size="large"
              sx={{
                color: 'white',
                borderColor: 'white',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
              onClick={() => history.push('/login')}
            >
              Already have an account?
            </Button>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#ff9800',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                },
              }}
              onClick={() => history.push('/contact')}
            >
              Contact Us
            </Button>
          </Box>
        </Box>
      </Grid>

      {/* Right side - Registration Form */}
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
        <Box
          sx={{
            my: 8,
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: '#ff9800' }}>
            <PersonAddIcon />
          </Avatar>
          <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
            Create Account
          </Typography>
        {error && (
            <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
            {error}
            </Alert>
        )}
        {success && (
            <Box sx={{ width: '100%', mb: 3 }}>
              <Alert severity="success" sx={{ mb: 2, width: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Account created successfully! 🎉
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  We've sent a verification email to <strong>{formData.email}</strong>. 
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Please check your inbox and click the verification link to verify your email address.
                </Typography>
                <Alert severity="warning" sx={{ mb: 2, width: '100%' }}>
                  <Typography variant="body2">
                    <strong>Email verification is required</strong> to continue. You must verify your email before accessing your dashboard or creating a profile.
                  </Typography>
                </Alert>
                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={async () => {
                      try {
                        const { api } = await import('../api');
                        // Use the unauthenticated endpoint that only requires email
                        await api.post('/auth/resend-verification-by-email', { 
                          email: formData.email 
                        });
                        setError('');
                        alert('Verification email resent! Please check your inbox.');
                      } catch (err) {
                        setError('Failed to resend email. Please try again later.');
                      }
                    }}
                    sx={{ minWidth: 200 }}
                  >
                    Resend Verification Email
                  </Button>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Check your email and click the verification link to continue.
                  </Typography>
                </Box>
              </Alert>
            </Box>
        )}
          {!success && (
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
          <TextField
              margin="normal"
              required
            fullWidth
              id="full_name"
              label="Full Name"
              name="full_name"
              autoComplete="name"
              value={formData.full_name}
            onChange={handleChange}
            />
            <TextField
            margin="normal"
            required
            fullWidth
              id="email"
              label="Email Address"
            name="email"
              autoComplete="email"
            value={formData.email}
            onChange={handleChange}
            />
            <TextField
            margin="normal"
            required
            fullWidth
              name="password"
            label="Password"
            type="password"
              id="password"
            value={formData.password}
            onChange={handleChange}
            />
            <TextField
            margin="normal"
            required
            fullWidth
              name="confirmPassword"
            label="Confirm Password"
            type="password"
              id="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
          />
          <FormControl fullWidth margin="normal">
              <InputLabel id="role-label">Role</InputLabel>
            <Select
                labelId="role-label"
                id="role"
              name="role"
              value={formData.role}
                label="Role"
              onChange={handleChange}
            >
              <MenuItem value="vendor">Vendor</MenuItem>
              <MenuItem value="organizer">Event Organizer</MenuItem>
            </Select>
          </FormControl>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  color="primary" 
                  required 
                />
              }
              label={
                <Typography variant="body2" component="span">
                  I agree to the{' '}
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={(e) => {
                      e.preventDefault();
                      setTosDialogOpen(true);
                    }}
                    sx={{ 
                      textDecoration: 'underline',
                      color: 'primary.main',
                      '&:hover': {
                        color: 'primary.dark',
                      }
                    }}
                  >
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={(e) => {
                      e.preventDefault();
                      setPrivacyDialogOpen(true);
                    }}
                    sx={{ 
                      textDecoration: 'underline',
                      color: 'primary.main',
                      '&:hover': {
                        color: 'primary.dark',
                      }
                    }}
                  >
                    Privacy Policy
                  </Link>
                </Typography>
              }
              sx={{ mt: 2, alignItems: 'flex-start' }}
            />
            <TermsOfService 
              open={tosDialogOpen} 
              onClose={() => setTosDialogOpen(false)} 
            />
            <PrivacyPolicy 
              open={privacyDialogOpen} 
              onClose={() => setPrivacyDialogOpen(false)} 
            />
          <Button
            type="submit"
            fullWidth
            variant="contained"
              sx={{
                mt: 3,
                mb: 2,
                bgcolor: '#ff9800',
                '&:hover': {
                  bgcolor: '#f57c00',
                },
              }}
            disabled={loading}
          >
              {loading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                'Create Account'
              )}
          </Button>
            <Grid container spacing={2}>
              <Grid item xs={12} sx={{ textAlign: 'center' }}>
                <Link href="/login" variant="body2">
                  Already have an account? Sign in
            </Link>
              </Grid>
            </Grid>
          </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default Register;